import { z } from '../zod';
import { FIELD_INPUT_EXAMPLES } from './examples';
import { FieldElementValidationEnum, FieldTypeValidationEnum } from './enum';
import { FieldPageParamsValidationSchema } from './params';
import { FORM_LAYOUT_FIELD_ORDER_RULE } from '../page/formLayout';

const ELEMENTS_WITHOUT_TYPE = new Set([
  'checkbox',
  'quantity',
  'tax-id',
  'po-number',
  'multiple-choice',
  'multi-select',
  'date-time',
  'date',
  'time',
  'textarea',
]);
const ELEMENTS_WITHOUT_TYPE_DESCRIPTION = Array.from(ELEMENTS_WITHOUT_TYPE)
  .map(element => `\`${element}\``)
  .join(', ');

const FieldDefaultValueInputSchema = z.object({
  enabled: z.boolean().nullish().meta({
    description: 'Whether a default value should be pre-filled for this field.',
    example: false,
  }),
  value: z.string().nullish().meta({
    description: 'Default value for the field when `enabled` is true.',
    example: 'Acme Inc.',
  }),
  values: z
    .array(z.string())
    .nullish()
    .meta({
      description:
        'Option values pre-ticked on a `multi-select` field when `enabled` is true (an option without a value is named by its label). Ignored on other elements.',
      example: ['google', 'facebook'],
    }),
});

export const FieldShowHideLogicInputSchema = z.object({
  enabled: z.boolean().nullish().meta({
    description: 'Whether conditional visibility is enabled for this field.',
    example: true,
  }),
  comparison: z.enum(['is', 'is_not', 'is_empty', 'is_not_empty']).nullish().meta({
    description:
      'Comparison operator used for conditional visibility checks. Trigger fields must be `select`, `multi-select`, `multiple-choice`, or `checkbox`. Checkbox triggers only support `is` and `is_not`. Use `is_empty` and `is_not_empty` only with `select`, `multi-select` and `multiple-choice` triggers. On a `multi-select` trigger, `is` means the option is among the chosen ones.',
    example: 'is',
  }),
  value: z.string().nullish().meta({
    description:
      'Value used by conditional visibility when the selected comparison requires one. For `select` and `multiple-choice` trigger fields, this must match the `key` or `id` of an option on the source field. For `checkbox` trigger fields with `is` or `is_not`, this field is required and must be `TRUE` or `FALSE`. During creation you may provide caller-defined keys; persisted responses return database IDs. Omit this field for `is_empty` and `is_not_empty`.',
    example: 'TRUE',
  }),
  element: z
    .object({
      elementTitle: z.string().nullish().meta({
        description: 'Optional display title of the source trigger field used by the condition.',
        example: 'Shipping Method',
      }),
      elementType: z.enum(['field']).nullish().meta({
        description: 'Type of source trigger element used in the condition.',
        example: 'field',
      }),
      elementId: z.string().nullish().meta({
        description:
          'The `key` or `id` of the source trigger field used in the condition. Must match the `key` or `id` of another field in the same batch. In the dashboard field editor, only `select`, `multiple-choice`, and `checkbox` fields can be chosen as trigger fields. During creation you may provide caller-defined keys; persisted responses return database IDs.',
        example: 'shipping-method',
      }),
    })
    .nullish(),
});

const FieldValueBoundaryInputSchema = z.object({
  enabled: z.boolean().nullish().meta({
    description: 'Whether this boundary rule is enabled.',
    example: false,
  }),
  value: z.string().nullish().meta({
    description: 'Boundary value represented as a string.',
    example: '1',
  }),
});

const FieldAllowedCountriesInputSchema = z.object({
  enabled: z.boolean().nullish().meta({
    description: 'Whether country restrictions are enabled for this field.',
    example: false,
  }),
  countries: z
    .array(z.string())
    .nullish()
    .meta({
      description: 'ISO alpha-2 country codes allowed for this field.',
      example: ['US', 'CA'],
    }),
});

const FieldAddressCollectValidationEnum = z.enum(['billing', 'billing_and_shipping']);

const FieldOptionInputValidationSchema = z.object({
  key: z.string().nullish().meta({
    description:
      'Optional caller-defined key for this option. Not persisted to the database. Required when `showHideLogic` on another field needs to reference this option via `showHideLogic.value`. Creation endpoints may use this key in place of a Mongo ObjectId; persisted responses return the resolved database ID.',
    example: 'physical-delivery',
  }),
  label: z.string().meta({
    description: 'Display label shown to customers for this option.',
    example: 'Label',
  }),
  value: z.string().nullish().meta({
    description: 'Optional internal value for this option. Defaults to the label when omitted.',
    example: 'value',
  }),
});

export const FieldInputObjectValidationSchema = z.object({
  key: z.string().nullish().meta({
    description:
      'Optional caller-defined key for this field. Not persisted to the database. Required when other fields reference this field in `showHideLogic.element.elementId`. Creation endpoints may use this key in place of a Mongo ObjectId; persisted responses return the resolved database ID.',
    example: 'shipping-method',
  }),
  label: z.string().min(1).max(200).meta({
    description: 'Form label for the input element.',
    example: 'Company Name',
  }),
  description: z.string().max(500).nullish().meta({
    description: 'Optional helper text shown below the field label.',
    example: 'We will use this on your invoice.',
  }),
  placeholder: z.string().max(200).nullish().meta({
    description: 'Specifies a short hint that describes the expected value of an input element',
    example: 'Enter your company name',
  }),
  element: FieldElementValidationEnum.nullish().meta({
    description: 'Input control used to render this field in checkout. Defaults to `text`.',
    example: 'text',
  }),
  type: FieldTypeValidationEnum.nullish().meta({
    description: `Used to store field data in a predictable manner. Please note that you must use Customer Name and Shipping Address if you wish to use any of the shipping address data types. Do not set \`type\` when \`element\` is one of: ${ELEMENTS_WITHOUT_TYPE_DESCRIPTION}.`,
    example: 'company-name',
  }),
  options: z
    .array(FieldOptionInputValidationSchema)
    .nullish()
    .meta({
      description:
        'Selectable options. Applies to `select`, `multi-select` and `multiple-choice` elements. Options on a `multi-select` need different values (an option without a value uses its label), and options on a `multiple-choice` need different labels.',
      example: [
        { label: 'Starter', value: 'starter' },
        { label: 'Pro', value: 'pro' },
      ],
    }),
  layout: z.enum(['dropdown', 'list']).nullish().meta({
    description:
      'How a multi-select field is drawn at checkout: `dropdown` (the default) or `list`, a list of checkboxes. Ignored on other elements.',
    example: 'list',
  }),
  required: z.boolean().nullish().meta({
    description: 'Whether this field must be completed before checkout can continue.',
    example: true,
  }),
  reference: z.string().max(36).nullish().meta({
    description:
      "Stable key that exports, Zapier, Google Sheets and webhooks use for this field's answers. Generated from the field type or label when omitted, and not updated when the label changes. Changing it breaks integrations that match on the old key, so only send it when the seller asks.",
    example: 'custom_field_1',
  }),
  disabled: z.boolean().nullish().meta({
    description:
      'Whether this field is switched off. A disabled field keeps its settings but is removed from the page entirely: it is not rendered, cannot be revealed with `show_fields`, is not prefilled and is never submitted.',
    example: false,
  }),
  hidden: z.boolean().nullish().meta({
    description: 'Whether this field is hidden from customers at checkout.',
    example: false,
  }),
  defaultValue: FieldDefaultValueInputSchema.nullish().meta({
    description: 'Default value settings for the field.',
  }),
  showHideLogic: FieldShowHideLogicInputSchema.nullish().meta({
    description: 'Conditional visibility rules for this field.',
  }),
  minValue: FieldValueBoundaryInputSchema.nullish().meta({
    description: 'Minimum allowed value settings for this field.',
  }),
  maxValue: FieldValueBoundaryInputSchema.nullish().meta({
    description: 'Maximum allowed value settings for this field.',
  }),
  limitAllowedCountries: FieldAllowedCountriesInputSchema.nullish().meta({
    description: 'Country restriction settings for country-based fields.',
  }),
  showSelectedDialCode: z.boolean().nullish().meta({
    description: 'Whether to display the selected international dial code in phone inputs.',
    example: true,
  }),
  order: z
    .number()
    .int()
    .min(0)
    .nullish()
    .meta({
      description: `Position of this field among the page's fields, from 0. When omitted, a new field goes after the current ones, or follows its position in \`fields\` when a page is created. ${FORM_LAYOUT_FIELD_ORDER_RULE}`,
      example: 0,
    }),
  collect: FieldAddressCollectValidationEnum.nullish().meta({
    description:
      'Whether to collect only a billing address or both billing and shipping. Applies to `address` elements. Defaults to `billing`.',
    example: 'billing_and_shipping',
  }),
  collectShippingPhone: z.boolean().nullish().meta({
    description:
      'Whether to collect a phone number alongside the shipping address. Applies to `address` elements when `collect` is `billing_and_shipping`. Defaults to `false`.',
    example: false,
  }),
  limitShippingCountries: FieldAllowedCountriesInputSchema.nullish().meta({
    description:
      'Restrict the shipping country list. Applies to `address` elements when `collect` is `billing_and_shipping`.',
  }),
  limitBillingCountries: FieldAllowedCountriesInputSchema.nullish().meta({
    description: 'Restrict the billing country list. Applies to `address` elements.',
  }),
  defaultCountry: z.string().length(2).nullish().meta({
    description:
      'ISO 3166-1 alpha-2 country code used as the default for `address` elements. Defaults to `US`.',
    example: 'US',
  }),
});

// zod 4 throws on .omit()/.partial() over an object that carries refinements,
// so variants derive from the raw object and apply this last.
export function withFieldInputRefinements<T extends z.ZodObject<z.ZodRawShape>>(schema: T): T {
  return schema.superRefine((value, ctx) => {
    const { element, type } = value as { element?: string | null; type?: string | null };

    if (type && ELEMENTS_WITHOUT_TYPE.has(element ?? 'text')) {
      ctx.addIssue({
        code: 'custom',
        path: ['type'],
        message: `Element "${element}" does not support a data type.`,
      });
    }
  }) as T;
}

export const FieldInputValidationSchema = withFieldInputRefinements(
  FieldInputObjectValidationSchema,
).meta({
  id: 'FieldInput',
  description: 'Input schema for a custom checkout field.',
  examples: FIELD_INPUT_EXAMPLES,
});

export const FieldCreateBodyValidationSchema = withFieldInputRefinements(
  FieldInputObjectValidationSchema.omit({ key: true }),
);

export const CreateFieldInputValidationSchema = withFieldInputRefinements(
  z.object({
    ...FieldPageParamsValidationSchema.shape,
    ...FieldInputObjectValidationSchema.omit({ key: true }).extend({
      showHideLogic: FieldShowHideLogicInputSchema.nullish().meta({
        description:
          'Conditional visibility rules for this field. `element.elementId` must be the ID of an existing field on the same page, and `value` an option ID on that field (or `TRUE`/`FALSE` for a checkbox). Read the IDs from the page detail response; create-time keys are rejected here.',
      }),
    }).shape,
  }),
);

export type CreateFieldInput = z.infer<typeof CreateFieldInputValidationSchema>;
